/**
 * The ByteGo school-onboarding checklist — every phase, section and step a
 * field agent walks a school through from the first meeting to go-live.
 *
 * CODE-TRUTHFUL. Each step names the real route, the real button labels and
 * the real behaviour of the app on 2026-10-06 (researched screen by screen;
 * the previous catalogue described menus that did not exist — "Fee
 * Categories", "Send Credentials", "Permission Matrix", "Run Preflight").
 * When the app changes, change the step here in the same commit; the step
 * `id` is the persisted key and must never be renamed.
 *
 * Ordering is the recommended order: a step's `dependsOn` only ever points
 * backwards. Steps with a `module` (or inside a phase with one) are skipped
 * automatically for schools that do not use that module.
 */
import type { OnboardingPhase, OnboardingSection, OnboardingStep } from "./types.js";

export const ONBOARDING_CHECKLIST_VERSION = "2026-10-06";

export const ONBOARDING_PHASES: OnboardingPhase[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // 1. KICK-OFF & FOUNDING ACCOUNT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_kickoff",
    title: "Kick-off & account",
    tagline: "Scope agreed, data collected, the school exists in CampusHub.",
    description:
      "Agree what the school is buying, collect the raw data you will import, and create the tenant: Control Center access code → /signup → /onboarding quickstart.",
    tone: "indigo",
    sections: [
      {
        id: "sec_kick_scope",
        title: "Scope the rollout",
        steps: [
          {
            id: "kick_scope",
            title: "Agree scope, point of contact and go-live date with the principal",
            summary: "A 30-minute sit-down that decides what this checklist contains.",
            owner: "agent",
            required: true,
            minutes: 45,
            how: [
              "Confirm the modules the school will use on day one: transport, GPS devices, hostel, online admissions, online fee payment, public website, ID cards. Set them on this tracker (Edit school) so the checklist adapts.",
              "Agree one point of contact at the school (usually the office admin) and their mobile — they will hold the founding admin login.",
              "Agree the go-live date and record it on the tracker. Working backwards: students imported one week before, parents onboarded three days before.",
              "Ask the approximate strength (students, teachers) and record it — it sizes the import and the SMS budget.",
            ],
            verify: [
              "Tracker shows the right module toggles, a contact person, a mobile and a target go-live date.",
              "The principal has named the person who will own the admin login.",
            ],
            pitfalls: [
              "Skipping this is how a school ends up with transport steps nobody needs and a go-live nobody agreed to.",
            ],
            tips: ["Take a photo of the school letterhead and fee circular now — you will need both in later phases."],
          },
          {
            id: "kick_data",
            title: "Collect the source data from the school",
            summary: "The spreadsheets and documents every later import depends on.",
            owner: "school",
            required: true,
            minutes: 60,
            dependsOn: ["kick_scope"],
            how: [
              "Student register as Excel or CSV — one row per child with at least Student name, Parent name and Parent phone. Admission number, class, section, roll number, date of birth, gender and Day Scholar / Boarding are strongly recommended.",
              "Staff list with Name and Employee ID (both required by the importer), plus email, mobile, role (Teacher / Staff / Principal), department and subject.",
              "The printed fee circular (every head, class-wise amounts, installment due dates), the bell schedule, the holiday list and the exam calendar.",
              "Logo as PNG/SVG (256×256 or larger, transparent) and the letterhead (1600×260 banner).",
            ],
            verify: [
              "You have the student Excel on your laptop and it has a parent phone on every row.",
              "You have the staff list with an Employee ID on every row.",
              "You have the fee circular, bell timings and holiday list (photo is fine).",
            ],
            pitfalls: [
              "Never retype a register. Ask the school to export from their old ERP or Excel.",
              "Dates typed as text like 03/04/2010 are read month-first by the importer. Ask for real Excel date cells or YYYY-MM-DD.",
            ],
          },
        ],
      },
      {
        id: "sec_kick_account",
        title: "Create the school",
        steps: [
          {
            id: "acct_code",
            title: "Get a signup access code from Control Center",
            summary: "A 12-character code, issued by ByteGo, is needed to create a school.",
            owner: "bytego",
            required: true,
            minutes: 5,
            how: [
              "Ask ByteGo ops to create a code in Control Center → Signup codes (add the school name as the note).",
              "Codes are shown as XXXX-XXXX-XXXX, are case-insensitive and ignore dashes. A code works once; a CONSUMED code is linked to the school it created.",
            ],
            verify: ["You have a PENDING code with the school's name on it."],
            pitfalls: [
              "Do not use the staging test code CAM-PUS-HUB on production — it only works on dev/staging.",
            ],
          },
          {
            id: "acct_signup",
            title: "Create the founding admin on /signup",
            summary: "Access code + name + work email + password. A 6-digit code arrives by EMAIL.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/signup",
            dependsOn: ["acct_code"],
            how: [
              "Open campushub.bytego.ai/signup on a laptop (the native app has no signup).",
              "Enter the access code, the admin's full name, their work email and a strong password (the page needs strength 2+; the server needs 8–128 characters).",
              "Enter the 6-digit code sent to that email (10-minute expiry, 5 attempts, resend after 30 s).",
            ],
            verify: [
              "The browser lands on /onboarding with the school form open.",
              "The admin can open the verification email themselves — it is their account, not yours.",
            ],
            pitfalls: [
              "There is no mobile number on signup and no SMS OTP. The admin's mobile is added later in My Profile.",
              "Use the school's working email. A personal Gmail the admin leaves with is a handover problem.",
            ],
          },
          {
            id: "acct_quickstart",
            title: "Finish quickstart on /onboarding",
            summary: "School name, code, board, address, phone, email — the tenant is created.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/onboarding",
            dependsOn: ["acct_signup"],
            how: [
              "School name exactly as on the affiliation certificate (2–200 characters).",
              "School code is auto-filled from the initials — change it now if the school has a known short code. It cannot be edited later from Settings.",
              "Board (CBSE / ICSE / IB / Cambridge / State Board / Other), full address, school phone and school email are required; city, state, pincode and socials are optional.",
              "Submit. The admin is signed in and lands on /dashboard with role ADMIN.",
            ],
            verify: [
              "Settings → School Profile → System Info shows the school code you expect.",
              "Record the school code on this tracker (Edit school → School code).",
            ],
            pitfalls: [
              "Quickstart creates NO academic year, classes, subjects or settings. Every module except Dashboard, Settings, Admissions, Communication, HR and a few others stays locked until classes AND subjects exist.",
              "If the code is taken, the app appends a random suffix silently — check System Info.",
            ],
          },
          {
            id: "acct_profile_phone",
            title: "Add the admin's mobile and set a sign-in PIN",
            summary: "Phone + PIN is how the admin will log in on the app without an email OTP.",
            owner: "agent",
            required: true,
            minutes: 5,
            route: "/settings/profile",
            dependsOn: ["acct_quickstart"],
            how: [
              "Settings → My Profile → add the admin's 10-digit mobile and save.",
              "In the Sign-in PIN panel choose Set a PIN (4–6 digits). The PIN belongs to the account, not the device.",
              "Dismiss or complete the PIN prompt that appears a few seconds after login — it is the same thing.",
            ],
            verify: [
              "Sign out and sign back in with mobile + PIN: no email code is asked.",
            ],
            pitfalls: [
              "Without a phone on the profile, phone login simply does not find the account.",
              "Admin-tier password logins always trigger a 6-digit email code; PIN logins skip it. Teach the PIN.",
            ],
          },
          {
            id: "acct_app_login",
            title: "Install the CampusHubAI app on the admin's phone and sign in",
            summary: "One generic store app for every school; the account decides the school.",
            owner: "agent",
            required: true,
            minutes: 10,
            dependsOn: ["acct_profile_phone"],
            how: [
              "Install CampusHubAI from the Play Store / App Store (there is no per-school app and no school code on login).",
              "Sign in with mobile + PIN. Allow notifications when asked — this registers the device for push.",
            ],
            verify: ["The admin dashboard opens in the app and a push permission was granted."],
            tips: ["Do this with the admin holding their own phone — it is their first impression of the product."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 2. SCHOOL PROFILE & TEAM
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_profile",
    title: "School profile & team",
    tagline: "Identity, branding and the leadership accounts.",
    description:
      "Everything under Settings → School Profile and Members & Roles: identity, contact, print header, terms, and the principal / admin invitations.",
    tone: "sky",
    sections: [
      {
        id: "sec_prof_identity",
        title: "Identity & contact",
        steps: [
          {
            id: "prof_identity",
            title: "Complete school identity: logo, board, school type",
            summary: "Settings → School Profile. Logo prints on receipts, cards and report cards.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/settings?tab=school",
            dependsOn: ["acct_quickstart"],
            how: [
              "Upload the logo (PNG/SVG, 256×256 recommended, transparent background).",
              "Board: CBSE / ICSE / STATE / IB (Settings uses these four values even if quickstart offered more).",
              "School type: Public / Private / Government / International. There is no Day / Boarding type — boarding is handled per student.",
              "Save. Only users with the 'can edit school profile' right see Save; the founding admin has it.",
            ],
            verify: ["The logo appears in the sidebar header and on a fee receipt preview."],
            pitfalls: ["A JPG with a white box looks wrong on coloured documents — ask for a transparent PNG."],
          },
          {
            id: "prof_contact",
            title: "Enter address, contact details and map pin",
            summary: "Printed on receipts, used by transport and the public website.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/settings?tab=school",
            dependsOn: ["acct_quickstart"],
            how: [
              "Contact: school email, phone, alternate phone, website.",
              "Location: street address, city, state, pincode. Add latitude/longitude if transport is in scope (also set in Transport → Settings).",
              "Online presence: Facebook, Instagram, X, LinkedIn, YouTube if the school has them.",
            ],
            verify: ["All contact fields are the school's public numbers, not the admin's personal mobile."],
          },
          {
            id: "prof_letterhead",
            title: "Set the print header, receipt lines and signatures",
            summary: "The letterhead banner that prints on receipts, due slips, report cards, certificates and hall tickets.",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/settings?tab=school",
            dependsOn: ["prof_identity"],
            how: [
              "Print/PDF header: upload the letterhead (1600×260), choose fit (fill / contain / cover) and height (8–50 mm). Tick 'My letterhead already shows the school name' if it does.",
              "Fee receipt lines: optional motto (max 160 characters) and foot line (max 80).",
              "Report card signatures: upload the principal and class-teacher signature images.",
            ],
            verify: ["Open any receipt PDF: header, motto and footer render as the school expects."],
          },
          {
            id: "prof_quarters",
            title: "Define the quarters / terms",
            summary: "Up to 4 dated quarters; used by curriculum pacing and report-card term logic.",
            owner: "agent",
            required: false,
            minutes: 5,
            route: "/settings?tab=school",
            dependsOn: ["acct_quickstart"],
            how: [
              "Scroll to the Quarters / Terms card at the bottom of School Profile.",
              "Enter a label and start/end dates per quarter (two terms = two quarters). Save.",
            ],
            verify: ["Quarter dates match the school's printed annual calendar."],
            pitfalls: ["There is no separate Academic Year or Terms screen; this card is the only place."],
          },
        ],
      },
      {
        id: "sec_prof_team",
        title: "Leadership accounts",
        steps: [
          {
            id: "prof_members",
            title: "Invite the principal and any other admins",
            summary: "Settings → Members & Roles → Invite member. Email = link; 10-digit phone = account with a PIN.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/settings/members",
            dependsOn: ["acct_quickstart"],
            how: [
              "Invite member → full name, email OR 10-digit mobile, role (Chairman / Admin / Director / Principal / Staff).",
              "Email invite: the person gets a 14-day link to /invitations/accept and sets their own password.",
              "Phone invite: the account is created immediately with a temporary 4-digit PIN. Nothing is texted — press Reset PIN on the member row to see a PIN and hand it over in person.",
              "Decide per member whether they may edit the school profile (toggle on the member row).",
            ],
            verify: [
              "The principal has signed in at least once (web or app) and sees the principal dashboard.",
              "Every admin-tier user has a mobile on their profile so phone + PIN works.",
            ],
            pitfalls: [
              "There are no custom roles, no permission matrix and no Accountant role. Office staff who handle fees are role STAFF.",
              "The phone-invite toast says 'PIN sent via SMS' — it is not. Use Reset PIN.",
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 3. ACADEMIC STRUCTURE (desktop wizard)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_academic",
    title: "Academic structure",
    tagline: "Year, classes, subjects, periods and the calendar — unlocks every module.",
    description:
      "The desktop-only wizard at /settings/classes-setup. Students, Teachers, Timetable, Attendance, Exams, Finance and Transport stay locked until at least one class and one subject exist.",
    tone: "amber",
    sections: [
      {
        id: "sec_acad_structure",
        title: "Classes & subjects",
        steps: [
          {
            id: "acad_year",
            title: "Pick the academic year in the wizard",
            summary: "There is no Academic Year table — the year is a label like 2026-27 chosen in the wizard.",
            owner: "agent",
            required: true,
            minutes: 5,
            route: "/settings/classes-setup?tab=classes",
            dependsOn: ["acct_quickstart"],
            how: [
              "Open Settings → Academic Setup → any card (desktop browser only; the wizard is hidden on phones and in the app).",
              "Use the year selector at the top: the current year (June start) plus the next four.",
              "Make sure the top-bar year selector of the app shows the same year — it is a per-browser choice.",
            ],
            verify: ["The wizard header and the top-bar selector both show the year the school is running."],
            pitfalls: [
              "Creating classes under the wrong year leaves the school 'not set up' in the year everyone else is looking at.",
              "Year rollover later is Settings → New Academic Year (preview, then promote; Chairman / Admin only).",
            ],
          },
          {
            id: "acad_classes",
            title: "Create every class with its sections",
            summary: "Preset chips Nursery → Class 12 and sections A–E; multi-select and save.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/settings/classes-setup?tab=classes",
            dependsOn: ["acad_year"],
            how: [
              "Tick every class the school runs (use the school's own naming — Grade 1 vs Class 1 — consistently).",
              "Tick the sections per class. Streams for 11–12 (Science / Commerce / Arts) are sections.",
              "Save — one class row per class, sections stored as a list.",
            ],
            verify: [
              "Students → the class filter lists every class and section.",
              "The setup lock on Students / Teachers / Timetable disappears once subjects are also in (next step).",
            ],
            pitfalls: ["There is no capacity per section anywhere; do not look for it."],
          },
          {
            id: "acad_subjects",
            title: "Build the subject catalogue",
            summary: "Name, unique code and type CORE / ELECTIVE / ACTIVITY, from presets grouped by stage.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/settings/classes-setup?tab=subjects",
            dependsOn: ["acad_year"],
            how: [
              "Pick from the preset catalogue (Pre-Primary / Primary / Middle / Secondary) or add custom subjects.",
              "One canonical name per subject (Maths, not Math + Mathematics) with a short code (MATH, ENG, SCI).",
              "Type: CORE (defaults to 5 periods/week in the timetable), ELECTIVE or ACTIVITY (default 3).",
            ],
            verify: ["No duplicate names; every subject the timetable will need exists."],
            pitfalls: ["Subject type does not affect report cards or pass/fail — only the timetable default load."],
          },
          {
            id: "acad_map",
            title: "Map subjects to each class",
            summary: "Which subjects each class studies. Unmapped subjects never appear for that class.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/settings/classes-setup?tab=subjects",
            dependsOn: ["acad_classes", "acad_subjects"],
            how: [
              "For each class, tick the subjects it studies (Class 1: English, Hindi, Maths, EVS, Art, PE…).",
              "Save per class. Teachers and periods per week are NOT set here — that is the Timetable wizard.",
            ],
            verify: ["Each class shows its subject list; the exam cycle later creates one exam per mapped subject."],
          },
        ],
      },
      {
        id: "sec_acad_time",
        title: "Periods & calendar",
        steps: [
          {
            id: "acad_periods",
            title: "Set the period schedule (bell timings) for every class",
            summary: "Start time, periods per day, duration and breaks — required before any timetable.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/settings/classes-setup?tab=periods",
            dependsOn: ["acad_classes"],
            how: [
              "Enter start time (default 08:30), periods per day, period length (default 45 min) and breaks (default Short Break after P4, Lunch after P6).",
              "Use 'apply to many classes' to copy one schedule to every class with the same bell.",
              "Set the daily assembly window if the school has one.",
            ],
            verify: [
              "Every class has a period config with times — the AI timetable fails with MISSING_PERIOD_TIMINGS otherwise.",
            ],
            pitfalls: [
              "The special-period picker in this step is NOT saved. Special periods (assembly, library, PT) are created on /timetable → Special periods.",
            ],
          },
          {
            id: "acad_calendar",
            title: "Add holidays, exam windows and events",
            summary: "Holidays suppress absent alerts and hide periods; exam windows constrain the AI planner.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/settings/classes-setup?tab=calendar",
            dependsOn: ["acad_year"],
            how: [
              "Holidays: add each one (there is no state-list import). The same entries can be added from /calendar → New event → Holiday.",
              "Exam windows: 'Add exam window' per cycle (unit tests, half-yearly, annual).",
              "Events & activities: annual day, sports day, PTMs — parents see them on their calendar.",
            ],
            verify: ["Cross-check against the school's printed annual calendar; count the holidays."],
            pitfalls: [
              "Holidays do not block attendance marking; they only skip alerts and hide the day's periods.",
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 4. STAFF
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_staff",
    title: "Teachers & staff",
    tagline: "Every teacher imported, signed in, and tagged to a subject.",
    description:
      "Import from the Teachers page template, hand over credentials, and add office staff. Subject loads per class are set later in the Timetable wizard.",
    tone: "pink",
    sections: [
      {
        id: "sec_staff_import",
        title: "Import",
        steps: [
          {
            id: "staff_template",
            title: "Download the staff template and fill it",
            summary: "Teachers → Template. Only Staff name and Employee ID are required.",
            owner: "school",
            required: true,
            minutes: 30,
            route: "/teachers",
            dependsOn: ["kick_data", "acad_subjects"],
            how: [
              "Teachers → Template downloads a blank .xlsx with the importer's columns; starred columns are required.",
              "Fill Name and Employee ID for everyone. Add Email, Mobile, Role (Teacher / Staff / Principal), Department, Designation, Subject, Qualification, Date of joining where known.",
              "A missing email becomes a synthetic staff address — real emails get an invite link.",
            ],
            verify: ["Every row has a unique Employee ID; the Subject column uses names from the catalogue."],
            pitfalls: ["Mobile is NOT checked for duplicates on import — the same number on two rows creates two accounts."],
          },
          {
            id: "staff_import",
            title: "Import the staff sheet",
            summary: "Upload → Map columns → Map subjects → Preview → Done.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/teachers",
            dependsOn: ["staff_template"],
            how: [
              "Teachers → Import (desktop). Map the columns, then match unknown subject names to the catalogue or create them.",
              "Review the preview; rows without an Employee ID are skipped; duplicates by Employee ID, identity or email are skipped.",
              "Import. Up to 10,000 rows.",
            ],
            verify: [
              "Teachers list count equals the sheet; roles are right (Teacher / Staff / Principal).",
              "Open three random profiles: department, subject and mobile are correct.",
            ],
          },
          {
            id: "staff_credentials",
            title: "Hand over staff sign-in credentials",
            summary: "Password Campus@<EmployeeID> for email logins, or phone + PIN via Reset PIN. Nothing is texted.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/teachers",
            dependsOn: ["staff_import"],
            how: [
              "Real email: the teacher receives a 14-day invite link to set a password; until then the default password is Campus@<EmployeeID>.",
              "Phone + PIN: open the teacher profile → credentials card → Reset PIN. The PIN shows once; it must be changed on first use.",
              "Print a per-teacher slip (name, login phone/email, PIN or default password) and hand it over at the training.",
            ],
            verify: ["Three teachers have signed in on their own phones with phone + PIN."],
            pitfalls: [
              "An email + password login for a teacher triggers an email OTP; phone + PIN does not. Teach phone + PIN.",
            ],
          },
          {
            id: "staff_support",
            title: "Add office staff and any extra admins",
            summary: "Fee counter, clerks and lab staff are role STAFF (Teachers → Add, or Members & Roles).",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/settings/members",
            dependsOn: ["prof_members"],
            how: [
              "Members & Roles → Invite member with role Staff (phone invite → Reset PIN to get the PIN).",
              "STAFF can collect fees, file corrections and run the kiosk; they cannot approve corrections or edit fee plans.",
            ],
            verify: ["The fee counter person can open Finance → Fee Collection and record a payment."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 5. STUDENTS & PARENTS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_students",
    title: "Students & parents",
    tagline: "Every child is in, every parent can sign in.",
    description:
      "The student import (desktop), student type, roll numbers and the parent credential rollout. Credentials are shown on screen and handed over — nothing is sent by SMS.",
    tone: "orange",
    sections: [
      {
        id: "sec_stu_import",
        title: "Student import",
        steps: [
          {
            id: "stu_prepare",
            title: "Prepare the student spreadsheet",
            summary: "Required per row: Student name, Parent name, Parent phone. Everything else is optional but valuable.",
            owner: "school",
            required: true,
            minutes: 90,
            dependsOn: ["kick_data", "acad_classes"],
            how: [
              "One sheet (only the first sheet is read), header row with 3+ filled cells; S.No columns are ignored.",
              "Columns the importer understands: Admission number, Student name, Class, Section, Roll no, Date of birth, Gender, Student type (Day Scholar / Boarding), Blood group, Father/Mother name and phone, Email, Address, Aadhaar, Category, Previous school, Emergency contact.",
              "Class names like 'Class 7', 'Grade VII', '7th Std', 'VII A', 'LKG' are all understood.",
              "Dates: real Excel date cells or YYYY-MM-DD text. Avoid dd/mm/yyyy text — days ≤ 12 are read as months.",
            ],
            verify: ["Every row has a parent phone; admission numbers are unique within the file."],
            pitfalls: [
              "Siblings SHOULD share the parent's phone — they become one parent account. The old rule 'no two students on one number' was wrong.",
              "Rows without a parent phone are skipped at Preview.",
            ],
          },
          {
            id: "stu_import",
            title: "Import the students (desktop)",
            summary: "Students → Import: Upload → Map columns → Preview → Done. Aim for 0 skipped rows.",
            owner: "agent",
            required: true,
            minutes: 45,
            route: "/students",
            dependsOn: ["stu_prepare"],
            how: [
              "Choose the mode: 'Previous year data' for students already in the school (no admission fee, no mid-year proration); 'New enrollment' only for fresh admissions.",
              "Map columns (synonyms in English, Hindi and Telugu are auto-matched; an AI pass helps). Fix missing parent name / phone / class / section inline at Preview.",
              "Set a fallback class for blank class cells if needed, then Import. Up to 10,000 rows; progress streams live.",
              "On Done, read the skipped-row reasons and download the generated-credentials CSV (students with generated usernames).",
            ],
            verify: [
              "Done screen: skipped count is 0, or every skipped row has a reason you accept.",
              "Students → class-wise counts match the school's enrolment register.",
            ],
            pitfalls: [
              "Importing is blocked until classes exist (409 CLASSES_NOT_CONFIGURED).",
              "Re-importing the same sheet is safe: existing admission numbers are matched, not duplicated; blank class/section is back-filled.",
              "Fee plans that already exist are applied to each new student automatically — set fees first if you want zero follow-up, or run Re-apply later.",
            ],
          },
          {
            id: "stu_type",
            title: "Set Day Scholar / Boarding for every student",
            summary: "Student type decides which fee plan bills the child. Admins are blocked until every student has one.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/students",
            dependsOn: ["stu_import"],
            how: [
              "Include the Student type column in the import, or fix it afterwards: the app shows a blocking overlay listing unclassified students until each is set.",
              "Day schools: everyone is Day Scholar.",
            ],
            verify: ["No overlay on the admin dashboard; Students filter by type shows the expected split."],
          },
          {
            id: "stu_verify",
            title: "Spot-check the imported data",
            summary: "Ten to twenty students across classes, plus class-wise totals.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/students",
            dependsOn: ["stu_import"],
            how: [
              "Filter by class; open random students: name spelling, class + section, DOB, parent name and phone.",
              "Ask each class teacher to glance through their own section list.",
            ],
            verify: ["Class-wise counts equal the register; no student sits in the wrong section."],
            tips: ["Errors found now cost minutes; after parents log in they cost trust."],
          },
          {
            id: "stu_roll",
            title: "Assign roll numbers per class",
            summary: "Students → filter one class → Roll numbers: order, start, prefix, padding.",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/students",
            dependsOn: ["stu_import"],
            how: [
              "Filter the directory to one class, click Roll numbers; choose the order (name / admission no / roster), start number, padding, girls-first if the school does that.",
              "Repeat per class. Individual rolls can be edited inline.",
            ],
            verify: ["Every class has unique, contiguous roll numbers (the bulk tool refuses duplicates)."],
          },
          {
            id: "stu_photos",
            title: "Add student photos",
            summary: "One at a time from the student profile camera badge; needed for ID cards (initials otherwise).",
            owner: "school",
            required: false,
            minutes: 120,
            route: "/students",
            dependsOn: ["stu_import"],
            how: [
              "Open a student → camera badge on the profile header → upload or take a photo.",
              "There is no bulk photo upload. Plan a class-by-class photo day if ID cards are in scope.",
            ],
            verify: ["Senior classes (or the ID-card batch) have photos; the rest can follow after go-live."],
          },
        ],
      },
      {
        id: "sec_stu_parents",
        title: "Parent & student sign-in",
        steps: [
          {
            id: "par_credentials",
            title: "Generate parent sign-in credentials",
            summary: "Parent = phone + PIN (shown once from the student profile) or phone + Parent@<AdmissionNo>.",
            owner: "agent",
            required: true,
            minutes: 60,
            route: "/students",
            dependsOn: ["stu_verify"],
            how: [
              "Each parent account is keyed on the mobile number; siblings share one account and one PIN.",
              "Student profile → Parent sign-in card → Generate / Reset PIN. The PIN is shown once; the same PIN is written to every parent account on that phone.",
              "Alternative that needs no clicking: the password Parent@<AdmissionNo> of the first child works with the parent's phone.",
              "Prepare class-wise credential slips (child, login phone, PIN or password, app store link).",
            ],
            verify: [
              "You have signed in as two parents (one with siblings) on the app with phone + PIN.",
            ],
            pitfalls: [
              "Nothing is sent by SMS — the 'Send credentials' button does not exist. The resend-invite action only emails parents who have an email.",
              "Parents with the app installed stop receiving absent-alert SMS; they get push instead.",
            ],
          },
          {
            id: "par_rollout",
            title: "Run the parent app rollout",
            summary: "Distribute slips through class teachers and WhatsApp groups; help parents install and sign in.",
            owner: "school",
            required: true,
            minutes: 240,
            dependsOn: ["par_credentials", "acct_app_login"],
            how: [
              "Class teachers hand out the slips (or the school posts them in class WhatsApp groups).",
              "Share the Play Store / App Store link; parents sign in with phone + PIN and allow notifications.",
              "Hold a parent help desk on two mornings for the ones who get stuck.",
            ],
            verify: [
              "Communications → Push shows device tokens for most parents (target 80%+ before go-live).",
            ],
            tips: ["Send the welcome announcement (Communication phase) only after this — unacknowledged notices block the parent's screen."],
          },
          {
            id: "stu_credentials",
            title: "Hand over student sign-in for senior classes",
            summary: "Student = phone + PIN (Student sign-in card) or username + Campus@<AdmissionNo>.",
            owner: "agent",
            required: false,
            minutes: 30,
            route: "/students",
            dependsOn: ["stu_verify"],
            how: [
              "Student profile → Student sign-in card → Generate / Reset PIN (shown once). The student's login phone is usually the parent's number.",
              "Or the generated username (firstname@<code>.in) with Campus@<AdmissionNo>; the app forces a PIN on first login.",
            ],
            verify: ["A Class 10 student can open My Diary and their timetable; they cannot see fees."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 6. FEES & PAYMENTS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_fees",
    title: "Fees & payments",
    tagline: "Every class has a plan, every student is billed, the counter can collect.",
    description:
      "Fee Structure is one plan per class per student type (Day Scholar / Boarding). Saving a plan bills the whole class automatically — there are no fee categories, no plan builder and no assign step.",
    tone: "green",
    sections: [
      {
        id: "sec_fee_structure",
        title: "Fee structure",
        steps: [
          {
            id: "fee_structure",
            title: "Enter the fee structure for every class",
            summary: "Finance → Fee Structure: Tuition, Admission, extra heads, installments with due dates.",
            owner: "agent",
            required: true,
            minutes: 60,
            route: "/finance?tab=Fee%20Structure",
            dependsOn: ["acad_classes", "kick_data"],
            how: [
              "Day Scholar tab: click a class row → Edit / Set fees. Fixed heads are Tuition and Admission; use 'Add Column' for Development, Exam, Lab, etc. (a transport column is refused — transport is billed per rider).",
              "Payment schedule: number of installments with a due date each (optionally a custom % split); 'Different schedule for a fee head' for admission-once + tuition-in-four.",
              "'Apply to All' copies one head's amount to every class of that type; still verify class-wise amounts against the circular.",
              "Saving a plan bills every existing student of that class. New students are billed on import / admission automatically.",
            ],
            verify: [
              "Settings → Academic Setup → Fee Structure card is green (every class has a plan).",
              "Open a student → fee statement shows the right heads, amounts and due dates.",
            ],
            pitfalls: [
              "Every installment needs a due date or the save is blocked.",
              "Editing a plan later does NOT re-bill students already billed — use the 'Re-apply to these students' banner.",
              "Once a plan has billed students its class / year / type are locked.",
            ],
          },
          {
            id: "fee_boarding",
            title: "Enter Boarding / Hostel fee plans",
            summary: "The Boarding tab adds a Boarding Fee head; it bills students whose type is Boarding.",
            owner: "agent",
            required: true,
            module: "hostel",
            minutes: 20,
            route: "/finance?tab=Fee%20Structure",
            dependsOn: ["fee_structure", "stu_type"],
            how: [
              "Boarding / Hostel tab → per class: Tuition, Admission, Boarding and any extra heads, with the installment schedule.",
              "Hostel room allocation's monthly fee is NOT billed; only the Boarding plan is.",
            ],
            verify: ["/finance/boarding-check reports no boarder without a boarding fee."],
          },
          {
            id: "fee_policy",
            title: "Set late-fee rules and billing policy",
            summary: "Settings → Fees: late fee (flat / % / per day, grace, cap), mid-year joiner billing, TC hold.",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/settings?tab=fees",
            dependsOn: ["fee_structure"],
            how: [
              "Late fees: add a rule (kind, amount, grace days, optional cap, which heads, academic year). Off until a rule exists; a nightly job accrues it.",
              "Billing policy: how mid-year joiners are prorated and whether the Transfer Certificate is held until dues are cleared.",
            ],
            verify: ["Rules match the fee circular's late-fee line exactly."],
          },
          {
            id: "fee_concessions",
            title: "Configure concession types and approval caps",
            summary: "Concessions are per-student approval requests; types carry caps per approver role.",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/settings/classes-setup?tab=concessions",
            dependsOn: ["fee_structure"],
            how: [
              "Add types (Staff ward, RTE, Sibling, Merit…) with a unit (₹ or %) and caps for Admin / Principal / Director. Chairman is unlimited.",
              "Grant per student from a fee row → Reduce outstanding due → Raise a concession; approve at /concession-approvals.",
            ],
            verify: ["A test concession request appears in Finance → Fee Concessions and can be approved."],
            pitfalls: ["There is no bulk concession and no % preset that auto-applies."],
          },
          {
            id: "fee_dues_import",
            title: "Import previous dues for a mid-year migration",
            summary: "Fee Collection → Import Dues: Admission No, Total, Concession, Paid, Due, Due as of.",
            owner: "agent",
            required: false,
            minutes: 45,
            route: "/finance",
            dependsOn: ["stu_import", "fee_structure"],
            how: [
              "Export the old balances from the previous system keyed by admission number.",
              "Finance → Import Dues (3-step wizard). Each balance becomes a 'Previous Fee Due' installment dated before the year so it is collected first.",
              "Payments already made this year are entered through Record Payment with a backdated payment date.",
            ],
            verify: ["Dues → By Student: a known defaulter shows the carried-forward amount."],
          },
          {
            id: "fee_verify",
            title: "Verify billing coverage",
            summary: "Every class has a plan, every student shows the right outstanding, no re-apply banner.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/finance?tab=Dues",
            dependsOn: ["fee_structure", "stu_import"],
            how: [
              "Dues → By Classroom: every class lists an outstanding figure; a ₹0 class with students means its plan was saved after import — open Fee Structure and Re-apply.",
              "Check five students across classes (one boarder if applicable) against the circular.",
            ],
            verify: ["No class with students shows ₹0 total billed unless it genuinely has no fees."],
          },
        ],
      },
      {
        id: "sec_fee_collect",
        title: "Collection & payments",
        steps: [
          {
            id: "fee_bank",
            title: "Add the school's bank / UPI accounts for manual payments",
            summary: "Settings → Payments: accounts tagged Fee deposits, with QR, UPI ID and payment link.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/settings?tab=payments",
            dependsOn: ["acct_quickstart"],
            how: [
              "Add a bank account tagged Fee deposits (and Expenses / Salary if used). Upload the UPI QR and enter the UPI ID / payment link.",
              "Parents see these under Finance → How to pay and upload proof; staff approve it in the manual-pay queue.",
            ],
            verify: ["Parent view → How to pay shows the QR / UPI the school actually uses."],
          },
          {
            id: "fee_cashfree",
            title: "Enable online fee payment (Cashfree)",
            summary: "ByteGo onboards the school as a Cashfree vendor (bank + KYC), then the vendor id is pasted in.",
            owner: "bytego",
            required: true,
            module: "onlinePayments",
            minutes: 30,
            route: "/settings?tab=payments",
            dependsOn: ["fee_bank"],
            how: [
              "Collect the school's settlement bank account and KYC documents; ByteGo ops creates the Easy Split vendor in the Cashfree dashboard (manual — no API).",
              "Settings → Payments → Online payments (Cashfree): paste the Vendor ID, switch 'Accept online payments' on, choose who bears the gateway charge (Parent / School) and the %.",
            ],
            verify: ["A parent sees the Pay online option and a ₹1 sandbox / live test succeeds."],
            pitfalls: ["Without a vendor id the API refuses with GATEWAY_VENDOR_NOT_SET; the toggle alone does nothing."],
          },
          {
            id: "fee_collect_test",
            title: "Train the counter: collect, receipt, day close, corrections",
            summary: "One real collection end to end with the person who will do it every day.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/finance",
            dependsOn: ["fee_verify", "staff_support"],
            how: [
              "Fee Collection → pick a student → Record Payment (mode, reference, date) → print the receipt.",
              "Transactions → Day close: count the cash and close the day; only Chairman / Admin can reopen.",
              "Show that a recorded payment cannot be edited — mistakes go through Finance → Corrections (Admin approves).",
            ],
            verify: ["A receipt PDF printed with the school header; the day closed; the staff member did it unaided."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 7. TIMETABLE
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_timetable",
    title: "Timetable",
    tagline: "Teachers tagged, AI-generated, conflict-free, published.",
    description:
      "The Setup Wizard at /timetable/setup: Review setup → Configure (teacher + periods/week per subject per class) → AI or manual → Save & Publish. Preflight runs automatically.",
    tone: "violet",
    sections: [
      {
        id: "sec_tt",
        title: "Generate & publish",
        steps: [
          {
            id: "tt_teachers",
            title: "Tag teachers and weekly periods per subject for every class",
            summary: "Timetable → Setup Wizard → Configure: subject → teacher, periods per week, per class-section.",
            owner: "agent",
            required: true,
            minutes: 60,
            route: "/timetable/setup",
            dependsOn: ["acad_map", "acad_periods", "staff_import"],
            how: [
              "Pick a class-section; the Configure step lists its mapped subjects. Choose the teacher for each and the periods per week (CORE defaults to 5, others 3; max 40).",
              "The inline checklist must be green: period schedule, subjects added, teacher per subject, unique names, subjects fit in the week, no teacher overloaded.",
              "Repeat for every class-section. There is no bulk-assign screen; this IS the assignment.",
            ],
            verify: ["Every class-section shows all six checks green in Configure."],
            pitfalls: [
              "Teachers → Edit → 'subjects' is a free-text department field and does not drive the timetable.",
              "Total periods per week must not exceed periods/day × days/week or the AI refuses (demand-exceeds-supply).",
            ],
          },
          {
            id: "tt_generate",
            title: "Generate timetables for all classes with AI",
            summary: "'Setup all missing classes with AI' runs preflight, generates and publishes each class.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/timetable",
            dependsOn: ["tt_teachers"],
            how: [
              "Timetable → 'Setup all missing classes with AI' (or 'AI setup all classes'). Preflight runs first and shows blockers: no-classes, no-subjects, no-teacher-for-subject, demand-exceeds-supply.",
              "Fix each blocker in Configure, press 'Re-check pre-flight', then 'Generate N timetables'. Each class is generated and published automatically.",
              "For one class, use the wizard → Choose method AI → preview → Save & Publish.",
            ],
            verify: ["All Timetables shows a published grid for every class-section; no class is 'missing'."],
            pitfalls: ["Bulk targets only class-sections without a timetable; it cannot pick a subset."],
          },
          {
            id: "tt_review",
            title: "Review grids, resolve clashes, confirm teacher schedules",
            summary: "A teacher clash on publish opens the conflict dialog — pick a free teacher and republish.",
            owner: "agent",
            required: true,
            minutes: 45,
            route: "/timetable/all",
            dependsOn: ["tt_generate"],
            how: [
              "Open each class grid; swap or move slots with the swap dialog; clear a slot if needed.",
              "If Save & Publish returns a clash (SLOT_CONFLICT), the dialog lists free teachers per cell — choose and publish again.",
              "Sign in as two teachers: My Schedule (/timetable/teacher) shows their week; the admin tier is notified on every publish.",
            ],
            verify: ["Two teachers and one parent confirm their timetable in the app."],
          },
          {
            id: "tt_special",
            title: "Add special periods (assembly, library, PT)",
            summary: "Timetable → Special periods manager — the only place they are saved.",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/timetable",
            dependsOn: ["acad_periods"],
            how: [
              "Open the Special periods manager on /timetable and add Assembly / Library / Sports with day and period.",
              "Regenerate or edit the affected grids afterwards.",
            ],
            verify: ["The special period appears on the class grid and the teacher schedule."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 8. ATTENDANCE
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_attendance",
    title: "Attendance",
    tagline: "Teachers mark, parents are told, the office can see.",
    description:
      "Manual register by default; AM/PM vs Full day in Kiosk Settings; absent alerts configured on the Communications page. Staff kiosk, face check-in and biometric devices are optional.",
    tone: "teal",
    sections: [
      {
        id: "sec_att",
        title: "Configure & test",
        steps: [
          {
            id: "att_mode",
            title: "Confirm the attendance mode (AM/PM or Full day)",
            summary: "Kiosk Settings on /attendance/kiosk: student mode, staff check-in mode, late-after time.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/attendance/kiosk",
            dependsOn: ["acad_classes"],
            how: [
              "As admin open /attendance/kiosk → Kiosk Settings: studentAttendanceMode AM_PM (default) or FULL_DAY; staff check-in tap or face; late-after time.",
              "There is no Settings → Attendance tab and no 'marking method' selector: manual, biometric and kiosk run side by side.",
            ],
            verify: ["A teacher's register shows one session (Full day) or two (AM/PM) per class as intended."],
          },
          {
            id: "att_alerts",
            title: "Switch on parent absent alerts",
            summary: "Communications → SMS → Attendance Alert Schedule (default OFF, 09:00) plus the master SMS switch.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/communications",
            dependsOn: ["att_mode"],
            how: [
              "Settings → Integrations → SMS → 'Send SMS messages' ON (see Communication phase).",
              "/communications → SMS channel → Attendance Alert Schedule: enable and set the send time.",
              "How it routes: parents WITH the app get a push; parents WITHOUT the app get an SMS at the configured time; skipped on holidays and for past-date corrections.",
            ],
            verify: ["Both switches are on and the time is after the school's marking window."],
            pitfalls: ["If the time has already passed when the register is saved, nothing goes out that day."],
          },
          {
            id: "att_test",
            title: "Run a live attendance test with a class teacher",
            summary: "Mark one child absent; the parent gets a push (app) or SMS (no app) — not both.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/attendance",
            dependsOn: ["att_alerts", "par_rollout", "tt_generate"],
            how: [
              "Sign in as a teacher → Attendance → mark their class; mark one test child Absent.",
              "Watch the parent phone: push within a minute; SMS only on production, at the scheduled time, only if that parent has no app account.",
              "Admin dashboard → attendance widget shows today's %.",
            ],
            verify: ["The parent received exactly one alert on the expected channel; the register shows the mark."],
            pitfalls: ["SMS is sent in production only; nothing leaves staging."],
          },
          {
            id: "att_staff",
            title: "Set up staff attendance (window, geofence, kiosk account)",
            summary: "Attendance → Staff → Settings: check-in window and geofence; School Profile creates the kiosk login.",
            owner: "agent",
            required: false,
            minutes: 20,
            route: "/attendance?tab=staff",
            dependsOn: ["staff_import"],
            how: [
              "Attendance → Staff → Settings: window start/end (default 05:30–22:00), late-after, timezone; geofence where staff may check in.",
              "Settings → School Profile → Attendance Kiosk Account: generate the KIOSK login (KSK-… password) for the gate tablet; it only opens /attendance/kiosk.",
            ],
            verify: ["A staff member checks in from the kiosk tablet or their phone and appears on today's staff list."],
          },
          {
            id: "att_biometric",
            title: "Register biometric / RFID devices",
            summary: "/biometric: register devices (fingerprint, face, RFID, iris); punches mark students present.",
            owner: "agent",
            required: false,
            minutes: 45,
            route: "/biometric",
            dependsOn: ["stu_import"],
            how: [
              "Register each device with its type; the vendor pushes punches to the /biometric/logs endpoints or the sync endpoint.",
              "A student's PUNCH_IN marks PRESENT in that day's register.",
            ],
            verify: ["A test punch appears as Present for that student."],
            pitfalls: ["Face recognition in CampusHub is for STAFF check-in only, not students."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 9. COMMUNICATION
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_comms",
    title: "Communication",
    tagline: "SMS switched on, push proven, the welcome notice sent.",
    description:
      "SMS, WhatsApp, email and push are platform-managed by ByteGo (one smsstanch account, one WhatsApp Business account, Firebase push). The school only flips the SMS master switch and sends announcements.",
    tone: "rose",
    sections: [
      {
        id: "sec_comm",
        title: "Channels & welcome",
        steps: [
          {
            id: "comm_sms_switch",
            title: "Switch on SMS for the school",
            summary: "Settings → Integrations → SMS → 'Send SMS messages'. No per-school gateway, API key or DLT sender.",
            owner: "agent",
            required: true,
            minutes: 10,
            route: "/settings/integrations",
            dependsOn: ["acct_quickstart"],
            how: [
              "Settings → Integrations → SMS Gateway: turn the master switch ON (default OFF). The provider / API key / sender fields are kept on file but not used — sending goes through ByteGo's pooled smsstanch account with sender CMPHUB and ByteGo's DLT templates.",
              "Check the SMS credit balance on /communications and tell ByteGo ops the expected volume (≈ 2 × student count for the first month).",
            ],
            verify: ["The switch is on; /communications shows a credit balance."],
            pitfalls: [
              "There is no 'send test SMS' button. Prove SMS with the attendance test instead.",
              "Confirm with ByteGo ops that the DLT template wording is right for this school before switching on — the template bodies are fixed per platform, not per school.",
            ],
          },
          {
            id: "comm_push_check",
            title: "Prove push notifications on a parent phone",
            summary: "Push is Firebase at platform level; the parent just installs, signs in and allows notifications.",
            owner: "agent",
            required: true,
            minutes: 10,
            dependsOn: ["par_rollout"],
            how: [
              "On a parent phone with the app: sign in, allow notifications, then have the admin post a short announcement to Parents.",
              "The push arrives within a minute. If not: phone Settings → Notifications → CampusHubAI allowed; sign out and in again to re-register the token.",
            ],
            verify: ["/communications → Push shows the device token count climbing; the test push arrived."],
            pitfalls: ["There is no Settings → Integrations → Push toggle; nothing to configure per school."],
          },
          {
            id: "comm_welcome",
            title: "Send the welcome announcement",
            summary: "Communication → Announcements → New: audience Parents, type general, attachment allowed.",
            owner: "school",
            required: true,
            minutes: 15,
            route: "/communication",
            dependsOn: ["comm_push_check"],
            how: [
              "Title 'Welcome to <School> on CampusHub'. Body: what the app does, the office helpline, the app store link.",
              "Audience: Parents (or All). Narrow to a class or section with target scopes if needed.",
              "Send AFTER the parent rollout: every active notice a user has not acknowledged opens a blocking overlay until they tap Acknowledge.",
            ],
            verify: ["Three parents confirm they saw and acknowledged it in the app."],
            pitfalls: ["There is no pinning and the composer has no expiry; keep announcements short and few."],
          },
          {
            id: "comm_whatsapp",
            title: "Set up WhatsApp templates",
            summary: "/communications/whatsapp: templates authored and submitted to Meta on ByteGo's WhatsApp Business account.",
            owner: "bytego",
            required: false,
            minutes: 30,
            route: "/communications/whatsapp",
            dependsOn: ["comm_sms_switch"],
            how: [
              "Templates tab → create and submit for approval; business-initiated messages need an APPROVED template, free text only inside a 24-hour reply window.",
              "Logs show sent / delivered / read per message (unlike SMS, which only reports gateway outcome).",
            ],
            verify: ["One approved template exists and a test broadcast shows Delivered."],
            pitfalls: ["One global number for every school; there is no per-school WhatsApp number."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 10. EXAMS, DIARY & LESSONS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_academics",
    title: "Exams, diary & lessons",
    tagline: "The first exam cycle exists; teachers can post diary and marks.",
    description:
      "Grade scales in Settings → Exams, the first exam cycle on /exams (one exam per class × subject), marks in Gradebook, report cards from /exams/report-cards, and the Diary (homework + classwork) teachers post every day.",
    tone: "blue",
    sections: [
      {
        id: "sec_exams",
        title: "Exams",
        steps: [
          {
            id: "exam_scales",
            title: "Set grade scales and assessment scheme",
            summary: "Settings → Exams: grade scales (CBSE 9-point preset), assessment schemes, grace policy.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/settings?tab=exams",
            dependsOn: ["acad_classes"],
            how: [
              "Grade scales: add per-grade scales with pass minimums and 'effective from' year, or apply the CBSE 9-point preset. The single default scale is also editable at Gradebook → Grading Scale Setup.",
              "Assessment schemes: weights per exam type, best-of-N (CBSE IX–X preset). Grace policy: max grace marks per paper.",
            ],
            verify: ["The principal confirms the scale matches the board's guideline before any marks are entered."],
            pitfalls: ["Changing the scale after marks exist changes every report card."],
          },
          {
            id: "exam_cycle",
            title: "Create and publish the first exam cycle",
            summary: "/exams → create → School Exam Cycle: name, type, dates, classes, max marks — exams auto-created per subject.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/exams",
            dependsOn: ["acad_map", "exam_scales", "acad_calendar"],
            how: [
              "Create → School Exam Cycle (admin only): name (Unit Test 1), type, year, date range, eligible classes, default max marks, per-subject overrides.",
              "It creates one exam per class × mapped subject. Use Subject Schedule to set dates per paper; every paper appears on the calendar automatically.",
              "Publish the cycle.",
            ],
            verify: ["Each class in scope lists its papers; a parent sees the exam dates on the calendar."],
          },
          {
            id: "exam_marks_demo",
            title: "Walk a teacher through marks → approval → report card",
            summary: "Gradebook Scores sheet (or Excel import) → submit → approve → publish; report cards from /exams/report-cards.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/gradebook",
            dependsOn: ["exam_cycle", "staff_credentials"],
            how: [
              "As a teacher: Gradebook → Scores → enter a few marks (or import the Excel) → Submit. As admin: approve and Publish — parents are notified on publish.",
              "/exams/report-cards: pick template (classic / minimal / compact) and design (term / annual); issue for a class from /exams/year-report-card.",
              "Hall tickets: /hall-tickets per class + exam, optional fee-defaulter hold.",
            ],
            verify: ["One report card PDF opens with the school header and the right scale."],
          },
        ],
      },
      {
        id: "sec_diary",
        title: "Diary, lessons & curriculum",
        steps: [
          {
            id: "hw_demo",
            title: "Teacher posts a Diary entry; parent sees it",
            summary: "/homework IS the Diary: Classwork and Homework columns, 'New Assignment' button.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/homework",
            dependsOn: ["staff_credentials", "par_rollout"],
            how: [
              "As a teacher: Diary → New Assignment → class, subject, title, due date, optional attachment → post. No subject assignment is needed; the class–subject link is created automatically.",
              "Students are notified; the parent sees it under Diary and 'What we did in class' for classwork.",
            ],
            verify: ["A parent phone shows the entry under Diary within a minute."],
            pitfalls: ["The daily diary digest to parents is off by default and has no web switch — it is an API-only setting; ask ByteGo if the school wants it."],
          },
          {
            id: "hw_lessons",
            title: "Show teachers the lesson log (Teachers Updates)",
            summary: "/lessons lists today's periods from the published timetable; 'Post update' feeds the Diary.",
            owner: "agent",
            required: false,
            minutes: 15,
            route: "/lessons",
            dependsOn: ["tt_generate", "hw_demo"],
            how: [
              "As a teacher open /lessons: each period that ran today is a row; Post update records classwork + homework into the Diary.",
              "Admins see /lessons admin overview and complaints.",
            ],
            verify: ["A teacher logs one period and it shows in the admin overview."],
          },
          {
            id: "curr_setup",
            title: "Import the curriculum per class-subject",
            summary: "/curriculum: build manually or import a PDF / photo; coverage tracking is always on.",
            owner: "school",
            required: false,
            minutes: 120,
            route: "/curriculum",
            dependsOn: ["acad_map"],
            how: [
              "Per class + subject: setup manually or import the syllabus PDF; approve & lock (DRAFT → SUBMITTED → LIVE).",
              "Year plan generation needs quarter dates, chapters and a published timetable for that subject.",
            ],
            verify: ["One subject shows chapters with Not started / In progress / Completed."],
            pitfalls: ["There is no 'enable coverage' switch and no single-school heatmap; Subject Pacing / Needs Attention are the views."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 11. TRANSPORT (module)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_transport",
    title: "Transport",
    tagline: "Vehicles, routes, drivers, riders — and the bus on the parent's map.",
    description:
      "Settings → fares → vehicles → routes & stops → schedules → drivers → riders (one at a time) → fee sync. GPS devices are optional.",
    tone: "slate",
    module: "transport",
    sections: [
      {
        id: "sec_tpt_setup",
        title: "Fleet & routes",
        steps: [
          {
            id: "tpt_settings",
            title: "Set transport settings and fares",
            summary: "/transport/settings (school pin, geofence, notify-ahead minutes) and fare slabs in the Academic Setup wizard.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/transport/settings",
            dependsOn: ["prof_contact"],
            how: [
              "/transport/settings: school latitude/longitude, stop geofence radius, minutes-ahead for parent alerts, speed limit, map renderer.",
              "/settings/classes-setup?tab=transport: fare slabs (from km, to km, yearly ₹) and/or a flat yearly fare per route. Stops created here carry a distance in km (the Transport → Routes stop form has no km field).",
            ],
            verify: ["A fare slab or flat fare exists; the school pin is on the map."],
          },
          {
            id: "tpt_vehicles",
            title: "Add every vehicle",
            summary: "Vehicles → Add: number, type (Bus / Van / Tempo), seats, tracking source, driver.",
            owner: "agent",
            required: true,
            minutes: 20,
            route: "/transport/vehicles",
            dependsOn: ["tpt_settings"],
            how: [
              "Enter registration number, type, seats, optional driver name + phone, status, tracking source (Phone / Device / Both / Disabled).",
              "Schedule maintenance: fitness, insurance, pollution and permit renewals are bookings with a date (there are no expiry fields on the vehicle).",
            ],
            verify: ["Every bus the school runs is listed with the right seat count."],
          },
          {
            id: "tpt_routes",
            title: "Create routes with stops and schedules",
            summary: "Routes → New: name, vehicle, driver, morning/evening times; stops with sequence, time and address or pin.",
            owner: "agent",
            required: true,
            minutes: 60,
            route: "/transport/routes",
            dependsOn: ["tpt_vehicles"],
            how: [
              "Per route: name, vehicle, driver, morning and evening times. Add stops in pickup order with a time and either an address (geocoded) or latitude/longitude.",
              "Schedules: morning / evening, days of the week, departure time, vehicle and driver.",
            ],
            verify: ["Each route shows its stops on the map in the right order with both schedules."],
            tips: ["Ask the driver for the real stop order — they know it."],
          },
          {
            id: "tpt_drivers",
            title: "Create driver logins",
            summary: "Drivers → Add: name + mobile → 6-digit access code shown once; tick the routes they may run.",
            owner: "agent",
            required: true,
            minutes: 15,
            route: "/transport/drivers",
            dependsOn: ["tpt_routes"],
            how: [
              "Add a driver (name, mobile). Note the 6-digit code — it is their PIN, shown once; reset from the row if lost.",
              "Tick the routes the driver may run. The driver signs in on the app with phone + code (no OTP) and lands on /drive.",
            ],
            verify: ["Each driver has signed in on their phone and sees their route."],
          },
        ],
      },
      {
        id: "sec_tpt_riders",
        title: "Riders & live tracking",
        steps: [
          {
            id: "tpt_students",
            title: "Assign riders to routes and stops, then sync transport fees",
            summary: "One student at a time (Routes → Assign Student, or student profile → Transport); Transport → Fees → Sync.",
            owner: "agent",
            required: true,
            minutes: 90,
            route: "/transport/routes",
            dependsOn: ["tpt_routes", "stu_import", "fee_structure"],
            how: [
              "Routes → Assign Student → student + stop (or Any stop). Or open the student profile → Transport tab.",
              "Each assignment adds a separate Transportation Fee row from the route's flat fare or the stop's distance slab; students with no matching fare are not charged.",
              "Transport → Fees: review 'never billed' riders and press Sync transport fees.",
            ],
            verify: ["Rider count equals the school's bus list; Transport → Fees shows no unbilled riders."],
            pitfalls: ["There is no bulk assign; get the route-wise student list from the office and go route by route."],
          },
          {
            id: "tpt_gps",
            title: "Register GPS devices and bind them to vehicles",
            summary: "/transport/devices: brand, serial, IMEI, SIM, vehicle. Only registered IMEIs are accepted by the bridge.",
            owner: "bytego",
            required: true,
            module: "gps",
            minutes: 45,
            route: "/transport/devices",
            dependsOn: ["tpt_vehicles"],
            how: [
              "Register a tracker: pick the brand (Mappls LX15 / iTriangle / Tata Elxsi / Atlanta / Concox GT06 / JT808 / TK103 / HTTP-HMAC), enter serial, IMEI, SIM and the vehicle.",
              "Point the device at ByteGo's bridge host and the brand's port (ask ByteGo ops). Binding switches the vehicle to Both (phone + device).",
            ],
            verify: ["Live Fleet shows the vehicle moving from the device feed, not only the driver phone."],
          },
          {
            id: "tpt_live_test",
            title: "Run a live trip and check the parent's map",
            summary: "Driver starts a trip in /drive; the parent sees the bus, the stop and the ETA.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/transport",
            dependsOn: ["tpt_drivers", "tpt_students", "par_rollout"],
            how: [
              "Driver: open /drive → start the morning trip (phone GPS every ~2 s while moving).",
              "Admin: Live Fleet shows the vehicle. Parent: /transport (rider view) shows the bus, the child's stop and the ETA; the notify-ahead alert fires near the stop.",
              "Try the SOS button once on a test trip and confirm the admin sees it.",
            ],
            verify: ["One full trip visible to admin and parent; the stop alert arrived."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 12. HOSTEL (module)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_hostel",
    title: "Hostel",
    tagline: "Rooms exist, boarders are allocated, boarding fees are billed.",
    description:
      "Rooms must be created by ByteGo ops (the web app has no create-room screen yet); allocation and checkout are in /hostel; fees come from the Boarding plan.",
    tone: "indigo",
    module: "hostel",
    sections: [
      {
        id: "sec_hostel",
        title: "Rooms & boarders",
        steps: [
          {
            id: "hostel_rooms",
            title: "Create the hostel rooms (ByteGo ops)",
            summary: "The web app only offers Allocate; rooms are created through the API by ByteGo ops.",
            owner: "bytego",
            required: true,
            minutes: 30,
            route: "/hostel",
            dependsOn: ["acct_quickstart"],
            how: [
              "Send ByteGo ops the room list: room number, floor, block, type (Single / Double / Triple / Dormitory), capacity, amenities.",
              "Ops creates them with POST /hostel/rooms for the school; they then appear on /hostel.",
            ],
            verify: ["/hostel lists every room with the right capacity."],
            pitfalls: ["There are no building or bed tables — a bed is a bed number on the allocation."],
          },
          {
            id: "hostel_allocate",
            title: "Allocate boarders to rooms and audit boarding fees",
            summary: "/hostel → Allocate room (student, room, bed, check-in date); /finance/boarding-check for fees.",
            owner: "agent",
            required: true,
            minutes: 60,
            route: "/hostel",
            dependsOn: ["hostel_rooms", "stu_type", "fee_boarding"],
            how: [
              "Allocate each boarder (student type Boarding) to a room and bed with the check-in date.",
              "Run /finance/boarding-check: every boarder must carry the Boarding fee; the allocation's monthly fee is informational only.",
            ],
            verify: ["Boarding check reports zero issues; the warden can see the room roster."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 13. ADMISSIONS (module)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_admissions",
    title: "Admissions",
    tagline: "The office can take an enquiry to an enrolled student.",
    description:
      "/admissions pipeline: Enquiry → Admission test → Application review → Fee pending → Enrolled. Fixed form, optional tests from the wizard, one-at-a-time enrol. Requires the fees module.",
    tone: "teal",
    module: "admissions",
    sections: [
      {
        id: "sec_adm",
        title: "Pipeline",
        steps: [
          {
            id: "adm_tests",
            title: "Prepare admission tests",
            summary: "Settings → Academic Setup → Admission Tests: AI-generated or PDF upload with an answer key.",
            owner: "agent",
            required: false,
            minutes: 30,
            route: "/settings/classes-setup?tab=admissionTests",
            dependsOn: ["acad_classes"],
            how: [
              "Per class: generate with AI or upload the paper PDF, then upload the answer key so scans can be AI-graded.",
              "Per applicant later: attach test, skip test, or grade from the scanned answer sheet.",
            ],
            verify: ["A test exists for each class the school admits into."],
          },
          {
            id: "adm_walkthrough",
            title: "Walk the office through the pipeline with a test applicant",
            summary: "New enquiry (or walk-in) → review → fee pending → Enrol; the student, admission number and fees are created.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/admissions",
            dependsOn: ["fee_structure", "staff_support"],
            how: [
              "Create an enquiry (guardian phone must be 10 digits); print the blank form or AI-scan a filled paper form.",
              "Move the card through the stages; Enrol creates the student, the admission number (<CODE>-<yy>-<NNN>), fees and concessions, and emails the parent an invite only if an email was given.",
              "Delete or mark the test applicant afterwards; hand the office the parent PIN flow from the Students phase.",
            ],
            verify: ["The office person enrols one applicant unaided and finds them under Students."],
            pitfalls: ["There is no form builder and no 'Enrol all'; Students → Add Student also routes through Admissions."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 14. DOCUMENTS & WEBSITE (modules)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_documents",
    title: "ID cards, certificates & website",
    tagline: "Printed cards in hand, certificates branded, the site live.",
    description:
      "/id-cards (choose a design and palette, issue per class, print sheets), /certificates (fixed catalogue + Branding tab) and /website (Design, Content, publish). All optional modules.",
    tone: "violet",
    sections: [
      {
        id: "sec_docs",
        title: "Documents",
        steps: [
          {
            id: "doc_idcards",
            title: "Choose the ID card design and issue cards per class",
            summary: "/id-cards → Settings: one of 40 designs + 16 palettes; Issue tab per class; Print & download for A4 duplex or card-printer PDF.",
            owner: "agent",
            required: true,
            module: "idCards",
            minutes: 45,
            route: "/id-cards",
            dependsOn: ["prof_identity", "stu_photos"],
            how: [
              "Settings tab: pick a portrait or landscape design and a palette (no drag-and-drop canvas).",
              "Issue tab: a whole class at a time (60 per round, auto-repeats with skip-existing). Photos come from the student's profile photo; no photo = initials.",
              "Print & download tab: A4 duplex sheets or card-printer PDF by class or batch. Holders see their card on /id-cards; nobody is notified.",
            ],
            verify: ["One printed test card scans at /verify-id and shows the right student."],
          },
          {
            id: "doc_certs",
            title: "Brand certificates and issue a test",
            summary: "/certificates → Branding (logo, crest, signatures); Issue from the fixed catalogue, 1–50 recipients.",
            owner: "agent",
            required: false,
            module: "idCards",
            minutes: 20,
            route: "/certificates",
            dependsOn: ["prof_identity"],
            how: [
              "Branding tab: logo, crest, principal signature. Official documents (Study / Conduct / Transfer) use the letterhead.",
              "Issue tab: choose a theme and up to 50 students; the family gets an Achievement notification and sees it under My achievements.",
            ],
            verify: ["A test certificate verifies at /v/<token>."],
          },
        ],
      },
      {
        id: "sec_web",
        title: "Website",
        steps: [
          {
            id: "web_site",
            title: "Build and publish the school website",
            summary: "/website (web only): Design (template), Content (pages), Live updates (posts); publish; URL carries ?school=CODE.",
            owner: "school",
            required: true,
            module: "website",
            minutes: 120,
            route: "/website",
            dependsOn: ["prof_identity", "prof_contact"],
            how: [
              "Design: pick a template (sky / mint / blossom / sand / aurora / nexus). Content: home, about, academics, admissions, gallery, testimonials, contact, principal message.",
              "Live updates: flyers, admission notices, events, news. Set status PUBLISHED.",
              "The site lives on the shared CampusHub site host with ?school=<CODE>; a custom domain needs ByteGo to configure CloudFront.",
            ],
            verify: ["Open website shows the published site with the school's logo and contact."],
            pitfalls: ["There is no <school>.bytego.ai subdomain any more."],
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // 15. GO-LIVE & HANDOVER
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: "ph_golive",
    title: "Go-live & handover",
    tagline: "Every role tested, staff trained, principal signed off.",
    description:
      "User acceptance by role, the training session, the handover pack and the formal sign-off that closes this tracker.",
    tone: "green",
    sections: [
      {
        id: "sec_uat",
        title: "Acceptance by role",
        steps: [
          {
            id: "live_uat_admin",
            title: "UAT as admin — every module shows the right data",
            summary: "Dashboard, Students, Teachers, Timetable, Finance, Attendance, Communication.",
            owner: "agent",
            required: true,
            minutes: 30,
            route: "/dashboard",
            dependsOn: ["stu_verify", "staff_import", "tt_generate", "fee_verify", "att_test", "comm_welcome"],
            how: [
              "Student and teacher counts match; every class has a published timetable; Dues → By Classroom has no ₹0 class with students; SMS switch on; today's attendance % on the dashboard.",
              "No setup lock or student-type overlay anywhere.",
            ],
            verify: ["You can tick each module above without a caveat."],
          },
          {
            id: "live_uat_teacher",
            title: "UAT as teacher — mark attendance, post diary, see schedule",
            summary: "A real teacher on their own phone.",
            owner: "agent",
            required: true,
            minutes: 20,
            dependsOn: ["att_test", "hw_demo", "tt_review"],
            how: ["Phone + PIN login; mark the register; post one Diary entry; open My Schedule."],
            verify: ["All three worked unaided; the parent of the test child got the alert."],
          },
          {
            id: "live_uat_parent",
            title: "UAT as parent — profile, attendance, fees, timetable, diary",
            summary: "A volunteer parent on their own phone; wrong data on day one breaks trust.",
            owner: "agent",
            required: true,
            minutes: 20,
            dependsOn: ["par_rollout", "fee_verify", "tt_review"],
            how: [
              "Check the child's profile, attendance, fee statement (right outstanding, How to pay shows the school's UPI), timetable, diary, calendar.",
              "Siblings: both children visible under one login.",
            ],
            verify: ["The parent says the fee figure is correct."],
          },
          {
            id: "live_uat_student",
            title: "UAT as student — diary, timetable, exams; no fee data",
            summary: "DPDP check: a student must not see fees or other students.",
            owner: "agent",
            required: false,
            minutes: 10,
            dependsOn: ["stu_credentials"],
            how: ["Sign in as a senior student: My Diary, timetable, exam schedule; confirm there is no Finance entry."],
            verify: ["No fee or other-student data is visible."],
          },
        ],
      },
      {
        id: "sec_handover",
        title: "Training & sign-off",
        steps: [
          {
            id: "live_training",
            title: "Run the staff training session",
            summary: "1–2 hours: attendance, diary, fee collection, announcements, reports. Record it.",
            owner: "agent",
            required: true,
            minutes: 120,
            dependsOn: ["live_uat_admin"],
            how: [
              "Teachers: phone + PIN login, register, Diary, My Schedule, lesson log. Office: fee collection, receipt, day close, corrections, announcements.",
              "Leave a one-page quick reference at the office desk and the recording link in the school WhatsApp group.",
            ],
            verify: ["Attendance sheet of who was trained; the recording link shared."],
          },
          {
            id: "live_handover",
            title: "Hand over the support pack",
            summary: "Credentials document, ByteGo support contacts, escalation path, a 30-day WhatsApp group.",
            owner: "agent",
            required: true,
            minutes: 30,
            dependsOn: ["live_training"],
            how: [
              "Share: founding admin email, school code, where PINs are reset (Members / Teachers / Students), ByteGo support WhatsApp and email.",
              "Create the WhatsApp group: admin, principal, ByteGo CSM, you — for the first 30 days.",
              "Record the school code, admin email and contact on this tracker.",
            ],
            verify: ["The admin has ByteGo support saved on their phone; the tracker has the school code."],
          },
          {
            id: "live_signoff",
            title: "Get the principal's go-live sign-off and book follow-ups",
            summary: "Walk the checklist with the principal, record the sign-off on this tracker, schedule day-7 and day-30 calls.",
            owner: "agent",
            required: true,
            minutes: 20,
            dependsOn: ["live_handover", "live_uat_parent", "live_uat_teacher"],
            how: [
              "Open this tracker with the principal and read the summary: students, teachers, timetable, fees, parents on the app.",
              "Press Sign off on the tracker and enter the principal's name — it is only enabled once every applicable step is complete.",
              "Put the day-7 and day-30 follow-up calls in your calendar and note them here.",
            ],
            verify: ["The tracker shows Signed off with the principal's name and date."],
          },
        ],
      },
    ],
  },
];

// ── Derived indexes ──────────────────────────────────────────────────────────

/** Every step id in recommended order. */
export const ALL_STEP_IDS: string[] = ONBOARDING_PHASES.flatMap((p) => p.sections.flatMap((s) => s.steps.map((st) => st.id)));

export const TOTAL_STEPS = ALL_STEP_IDS.length;

/** stepId → step */
export const STEP_INDEX: ReadonlyMap<string, OnboardingStep> = new Map(
  ONBOARDING_PHASES.flatMap((p) => p.sections.flatMap((s) => s.steps.map((st) => [st.id, st] as const))),
);

/** stepId → 1-based number shown in the UI. */
export const STEP_NUMBERS: ReadonlyMap<string, number> = new Map(ALL_STEP_IDS.map((id, i) => [id, i + 1] as const));

const PHASE_BY_STEP = new Map<string, OnboardingPhase>();
const SECTION_BY_STEP = new Map<string, OnboardingSection>();
for (const phase of ONBOARDING_PHASES) {
  for (const section of phase.sections) {
    for (const step of section.steps) {
      PHASE_BY_STEP.set(step.id, phase);
      SECTION_BY_STEP.set(step.id, section);
    }
  }
}

export function phaseOf(stepId: string): OnboardingPhase | undefined {
  return PHASE_BY_STEP.get(stepId);
}

export function sectionOf(stepId: string): OnboardingSection | undefined {
  return SECTION_BY_STEP.get(stepId);
}

export function phaseById(id: string): OnboardingPhase | undefined {
  return ONBOARDING_PHASES.find((p) => p.id === id);
}
